import { useEffect, useRef, useState } from 'react';
import './VisualIvrLoadingSplash.css';

export interface AvatarLoadingSplashProps {
  headline?: string;
  body?: string;
  hint?: string | null;
  portraitUrl?: string | null;
  avatarName?: string | null;
  previewText?: string | null;
  contained?: boolean;
}

export function AvatarLoadingSplash({
  headline = 'Please wait...',
  body,
  hint = null,
  portraitUrl,
  avatarName,
  previewText,
  contained = false
}: AvatarLoadingSplashProps = {}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<'full' | 'compact' | 'mini'>(contained ? 'compact' : 'full');
  const resolvedBody =
    body
    ?? (avatarName ? `${avatarName} is getting ready...` : 'Your virtual assistant is loading');
  const trimmedPreview = typeof previewText === 'string' ? previewText.trim() : '';
  const showPreview = trimmedPreview.length > 0;

  useEffect(() => {
    if (!contained || !rootRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const classify = (width: number, height: number) => {
      const shortest = Math.min(width, height);
      if (height < 150 || width < 190 || shortest < 150) return 'mini';
      if (height < 280 || width < 300 || shortest < 260) return 'compact';
      return 'full';
    };

    const update = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setLayout(classify(rect.width, rect.height));
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return update();
      const { width, height } = entry.contentRect;
      setLayout(classify(width, height));
    });

    observer.observe(rootRef.current);
    update();
    return () => observer.disconnect();
  }, [contained]);

  return (
    <div
      ref={rootRef}
      className={`camlin-visual-ivr-splash${contained ? ` camlin-avatar-loading--contained camlin-avatar-loading--${layout}` : ''}`}
      data-camlin-avatar-loading-layout={contained ? layout : 'full'}
      role="status"
      aria-live="polite"
      data-testid="avatar-loading-splash"
    >
      <div className="camlin-visual-ivr-splash__content">
        {portraitUrl ? (
          <div className="camlin-visual-ivr-splash__portrait-wrap" aria-hidden="true">
            <img
              src={portraitUrl}
              alt=""
              className="camlin-visual-ivr-splash__portrait"
              onError={(event) => {
                const img = event.currentTarget;
                img.style.display = 'none';
                const wrap = img.parentElement;
                if (wrap) wrap.style.display = 'none';
              }}
            />
            <div className="camlin-visual-ivr-splash__portrait-spinner" />
          </div>
        ) : (
          <div className="camlin-visual-ivr-splash__spinner" aria-hidden="true" />
        )}
        <h2 className="camlin-visual-ivr-splash__headline">{headline}</h2>
        <p className="camlin-visual-ivr-splash__body">{resolvedBody}</p>
        {showPreview ? (
          <p className="camlin-visual-ivr-splash__preview">
            <span className="camlin-visual-ivr-splash__preview-label">
              {avatarName ? avatarName.toUpperCase() : 'ASSISTANT'}
            </span>
            <span className="camlin-visual-ivr-splash__preview-text">
              {trimmedPreview}
            </span>
          </p>
        ) : null}
        {hint ? (
          <p className="camlin-visual-ivr-splash__hint">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

export default AvatarLoadingSplash;
